import {
  Directive,
  EventEmitter,
  Input,
  NgZone,
  Output,
  Renderer2,
  ElementRef,
  ComponentFactoryResolver,
  ViewContainerRef,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';
import { DataTablePreference } from 'src/app/core/models/user-preference';
import { AuthenticationService } from 'src/app/core/services/authentication-service.service';
import { UserService } from 'src/app/core/services/user-service.service';
import { deepCopy, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { DateTableColumn } from '../../core/models/date-table-column';
import { SessionStorageService } from './../../core/services/session-storage.service';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { CustomRowsPerPageComponent } from '../components/custom-rows-per-page/custom-rows-per-page.component';
import { FilterMatchMode, FilterMetadata, FilterOperator } from 'primeng/api';
import { ObjectUtils } from 'primeng/utils';

@Directive({
  selector: '[laasDataTableSetting]'
})
export class DataTableSettingDirective {

  @Input() tableKey: string;
  @Input() defaultColumns: any[];
  @Input() expandable: boolean;
  @Input() selectable: boolean;
  @Input() rowsPerPageOptions: number[] = [5, 10, 25, 50, 100];
  @Output() setPageSize: EventEmitter<any>;
  selectedCols: any;
  tempTableColumns: any[] = [];
  // 固定在 header 前幾行的 width
  @Input() preHeaderDefaultWidths: number[] = [];
  // 固定在 header 尾巴的 width
  @Input() tailHeaderDefaultWidths: number[] = [];
  @Input() columnDefaultWidth: number = 100;
  customRowsPerPage: number | null = null;
  customRowsPerPageComponent: CustomRowsPerPageComponent | null = null;

  constructor(
    private zone: NgZone,
    private table: Table,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private sessionStoreageService: SessionStorageService,
    private commonDataService: CommonDataService,
    private translateService: TranslateService,
    private el: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,) {
    this.tableKey = '';
    this.defaultColumns = [];
    this.selectedCols = this.defaultColumns;
    this.selectable = false;
    this.expandable = false;
    this.setPageSize = new EventEmitter();
    this.table = table;
  }

  @Input() get selectedColumns(): DateTableColumn[] {
    return this.selectedCols;
  }


  get dataTablePreference(): DataTablePreference | null {
    return this.userService.preference.dataTablePreferences.find(x => x.tableName === this.tableKey) ?? null;
  }

  ngOnInit(): void {

    this.table.resizableColumns = true;
    this.table.columnResizeMode = 'expand';
    this.table.reorderableColumns = true;
    this.table.rowHover = false;
    this.table.paginator = true;
    this.table.rows = 5;
    this.table.rowsPerPageOptions = this.rowsPerPageOptions;
    this.table.scrollable = true;
    this.table.scrollHeight = '60vh';
    this.table.autoLayout = false;
    this.table.sortMode = 'single';
    this.table.styleClass = 'p-datatable-sm p-datatable-striped p-datatable-gridlines';
    this.table.tableStyle = {'min-width': '50rem', 'table-layout': 'fixed'};
    this.table.onStateSave.subscribe((event: any) => this.saveTablePreference(event));
    this.table.onColReorder.subscribe((event: any) => this.colReordered(event));
    this.table.onColResize.subscribe((event: any) => this.colResized(event));
    this.table.tableService.valueSource$.subscribe((event: any) => this.valueChange(event));
    this.table.onFilter.subscribe((event: any) => this.filter(event));
    this.translateService.onLangChange.subscribe(() => {
      this.pageSummaryTranslation();
    });
    this.pageSummaryTranslation();

    this.table.stateStorage = 'session';
    if (isNullOrEmpty(this.tableKey) === false) {
      this.table.stateKey = this.tableKey as string;
    }
    if (this.selectable) {
      this.preHeaderDefaultWidths.push(50);
    }
    if (this.expandable) {
      this.preHeaderDefaultWidths.push(50);
    }

    this.table.columns = this.defaultColumns;

    this.fetchDefaultRowsPerPageOptions();
  }

  ngAfterViewInit(): void {
    this.zone.run(() => {
      setTimeout(() => {
        this.loadTablePreference();
        this.addPaginatorBottomTemplate();
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(changes);
    if(changes['defaultColumns'] && !changes['defaultColumns'].firstChange) {
      this.table.columns = changes['defaultColumns'].currentValue;
    }
  }

  pageSummaryTranslation(): void{
    this.translateService.get('Common.Table.PageSummary')
    .subscribe({
      next: result => {
        this.table.showCurrentPageReport = true;
        this.table.currentPageReportTemplate = result;
      }
    });
  }

  /**
   * 根據 this.tempTableColumns 的 display 去顯示 this.table.columns
   */
  syncTableColumnsDisplay() {
    this.table.columns = this.table.columns?.filter(col => this.tempTableColumns.find(x => x.field === col.field).display);
  }
  /**
   * 根據 this.tempTableColumns 的 order 去排序 this.table.columns
   */
  syncTableColumnsOrder() {
    const fieldOrderMap = new Map<string, number>(this.tempTableColumns.map(col => ([col.field, col.order])));
    this.table.columns?.sort((a,b) => {
      let aOrder = fieldOrderMap.get(a.field);
      let bOrder = fieldOrderMap.get(b.field);
      if(isNullOrEmpty(aOrder) && isNullOrEmpty(bOrder)) {
        return 0;
      }
      return ( (aOrder ?? Infinity) - (bOrder ?? Infinity))
    })
  }
  /**
   * 同步更新 this.tempTableColumns
   * 若存在於 this.table.columns 則 display = true
   */
  syncTempTableColumnsDisplay() {
    const existsCol =  this.table.columns?.map(col => col.field) ?? [];
    this.tempTableColumns.forEach(col => {
      col.display = existsCol.includes(col.field);
    });
  }
  /**
   * 根據畫面變動的 this.table.columns 情況去更新 this.tempTableColumns 的 order
   * 若顯示的 order 為 3,2,7,1 根據這幾個 field 改為 1,2,3,7, 這樣不影響後續4,5,6,8再插入的位置
   */
  syncTempTableColumnsOrder() {
    const existFieldMapIndex = new Map<string, number>(this.table.columns?.map((col, index) => ([col.field, index])));
    const targetFields = this.tempTableColumns.filter(x => existFieldMapIndex.has(x.field) );
    const oldOrder = targetFields.map(x => x.order);
    targetFields.sort((a,b) => {
      let aIndex = existFieldMapIndex.get(a.field);
      let bIndex = existFieldMapIndex.get(b.field);
      if(isNullOrEmpty(aIndex) && isNullOrEmpty(bIndex)) {
        return 0;
      }
      return ( (aIndex ?? Infinity) - (bIndex ?? Infinity))
    });
    const newOrder = [...oldOrder].sort((a,b) => a-b);
    // set 入
    targetFields.forEach((col, index) => {
      col.order = newOrder[index];
    });
    // 重新排序
    this.tempTableColumns.sort((a,b) => a.order - b.order);
  }

  /**
   * 由 tempTableColumns 的display 存在的col取得 width string,
   * 使用前可以先確認 tempTableColumns 的 display與 tabale.columns 已同步
   */
  getWidths(): string {
    this.tempTableColumns.sort((a,b)=> a.order - b.order);
    let widthArr = this.tempTableColumns.filter(col => col.display).map(col => col.width) ?? [];
    return this.preHeaderDefaultWidths.concat(widthArr).concat(this.tailHeaderDefaultWidths).join(",");
  }
  /**
   * 若不存在則初始化
   * 若必要屬性為空則初始化
   */
  initTempTableColumns() {
    if(!this.tempTableColumns || this.tempTableColumns.length === 0) {
      this.tempTableColumns = deepCopy(this.defaultColumns) || [];
      this.tempTableColumns.forEach((col, index) => {
        if (col.order === undefined) {
          col.order = index;
        }
        if (col.field === '__delete') {
          col.width = col.width?? 70;
        } else {
          col.width = col.width?? this.columnDefaultWidth;
        }
        // dispay 預設 true
        if(isNullOrEmpty(col.display)) {
          col.display = true;
        }
      });
    }
  }

  colReordered(event: any): void {
    this.initTempTableColumns();
    this.syncTableColumnsDisplay();
    this.syncTempTableColumnsOrder();
    this.table.columnWidthsState = this.getWidths();
    this.table.tableWidthState = this.table.columnWidthsState.split(',').reduce((sum: number, width: string) => sum += parseInt(width, 10), 0) + '';

  }

  colResized(event: any): void {
    this.initTempTableColumns();
    this.syncTempTableColumnsDisplay();
    const field = event.element.getAttribute('field');
    const targetCol = this.tempTableColumns.find(x => x.field === field);
    if (targetCol) {
      targetCol.width = event.element.offsetWidth;
    }
    this.table.columnWidthsState = this.getWidths();
    this.table.tableWidthState = this.table.columnWidthsState.split(',').reduce((sum: number, width: string) => sum += parseInt(width, 10), 0) + '';
  }

  private saveTablePreference(event: any): void {
    this.initTempTableColumns();
    this.syncTempTableColumnsDisplay();
    // console.log("this.tempTableColumns: ", this.tempTableColumns);
    // console.log("this.table.columns: ", this.table.columns);
    if (isNullOrEmpty(this.tableKey) || !this.authenticationService.isAuthenticated) {
      return;
    }

    // 儲存local storage的user preference
    const state: any = {};
    state.sort = { sortField: event.sortField, sortOrder: event.sortOrder };
    state.columns = this.tempTableColumns;
    state.rows = event.rows;
    state.columnWidths = this.getWidths();
    state.rowsPerPageOptions = this.rowsPerPageOptions;

    const userPreference = this.userService.preference;

    let dataTablePreference = userPreference?.dataTablePreferences?.find(x => x.tableName === this.tableKey);
    if (dataTablePreference) {
      dataTablePreference.state = state;
    } else {
      dataTablePreference = { tableName: this.tableKey as string, state };
      userPreference.dataTablePreferences.push(dataTablePreference);
    }

    this.userService.preference = userPreference;

    // 載入user preference
    this.loadTablePreference();
  }

  private loadTablePreference(): void {
    this.initTempTableColumns();
    // 新畫面沒有自定義，使用預設寬度
    if (isNullOrEmpty(this.tableKey) || isNullOrEmpty(this.dataTablePreference)) {
      // this.tempTableColumns = JSON.parse(JSON.stringify(this.table.columns));
      let tempColumnWidthsStateArray = this.tempTableColumns.map((col) => col.width ?? this.columnDefaultWidth) ?? [];
      if (this.selectable) {
        tempColumnWidthsStateArray.splice(0, 0, 50);
      }
      if (this.expandable) {
        tempColumnWidthsStateArray.splice(0, 0, 50);
      }
      let tempColumnWidthsState = tempColumnWidthsStateArray.join(',');

      this.table.columnWidthsState = tempColumnWidthsState;
      this.table.tableWidthState = this.table.columnWidthsState.split(',').reduce((sum: number, width: string) => sum += parseInt(width, 10), 0) + '';
      this.table.restoreColumnWidths();

      return;
    }

    // 拿session storage的preference
    const srcState = this.sessionStoreageService.getItem(this.tableKey as string) ?? { first: 0, rows: 5, selection: [] };

    if (srcState.selection){
      srcState.selection = [];
    }

    const preference = this.dataTablePreference as DataTablePreference;
    if (isNullOrEmpty(preference.state?.columns) || preference.state.columns.length === 0) {
      return;
    }

    let columns: any[] = preference.state.columns;
    columns = columns.sort((a, b) => a.order > b.order ? 1 : -1);

    columns.forEach((col) => {

      const tempCol = this.tempTableColumns.find(x => x.field === col.field);
      if (tempCol) {
        tempCol.order = col.order;
        tempCol.display = col.display;
        tempCol.width = col.width ?? this.columnDefaultWidth;
      }

      const defaultCol = this.defaultColumns.find(x => x.field === col.field);
      if (defaultCol) {
        defaultCol.order = col.order;
      }
    });
    // 根據 state 去過濾, 順便讓 this.defaultColumns 與 this.table.columns 斷開引用
    this.syncTableColumnsDisplay();
    this.syncTableColumnsOrder();
    // 欄位 toggle options 也排序
    this.defaultColumns.sort((a,b) => a.order - b.order);

    this.table.rows = preference.state?.rows;

    this.setPageSize.emit({
      rows: this.table.rows
    });

    const rppo = preference.state?.rowsPerPageOptions;
    if (rppo && rppo.length > 0) {
      this.rowsPerPageOptions = rppo;
    }

    if (isNullOrEmpty(preference.state.sort)) {
      srcState.sortField = preference.state.sort.sortField;
      srcState.sortOrder = preference.state.sort.sortOrder;
    }

    srcState.columnWidths = preference.state.columnWidths;

    // 儲存session storage的preference
    this.sessionStoreageService.setItem(this.tableKey as string, srcState);

    // 刷新table欄位寬度
    this.table.columnWidthsState = srcState.columnWidths;
    this.table.tableWidthState = srcState.columnWidths.split(',').reduce((sum: number, width: string) => sum += parseInt(width, 10), 0) + '';
    this.table.restoreColumnWidths();
  }

  valueChange(event: any[]): void {
    if (!this.customRowsPerPageComponent) { return; }
    this.customRowsPerPageComponent.disabled = !event.length;
  }

  /**
   * copy partial code from primeng v17 source code table.ts:_filter()
   * customized and replaced executeLocalFilter method
   * 
   */
  filter(event: any): void {
    if(!this.table.lazy) {
      return;
    }
    
    if (!this.table.value) {
        return;
    }
    if (!this.table.hasFilter()) {
      this.table.filteredValue = null;
      return;
    }
    
    let globalFilterFieldsArray;
    if (this.table.filters['global']) {
        if (!this.table.columns && !this.table.globalFilterFields) throw new Error('Global filtering requires dynamic columns or globalFilterFields to be defined.');
        else globalFilterFieldsArray = this.table.globalFilterFields || this.table.columns;
    }

    this.table.filteredValue = [];

    for (let i = 0; i < this.table.value.length; i++) {
      let localMatch = true;
      let globalMatch = false;
      let localFiltered = false;

      for (let prop in this.table.filters) {
        if (this.table.filters.hasOwnProperty(prop) && prop !== 'global') {
          localFiltered = true;
          let filterField = prop;
          let filterMeta = this.table.filters[filterField];

          if (Array.isArray(filterMeta)) {
            for (let meta of filterMeta) {
              
              localMatch = this.executeLocalFilter(filterField, this.table.value[i], meta);

              if ((meta.operator === FilterOperator.OR && localMatch) || (meta.operator === FilterOperator.AND && !localMatch)) {
                  break;
              }
            }
          } else {
            localMatch = this.executeLocalFilter(filterField, this.table.value[i], <any>filterMeta);
          }

          if (!localMatch) {
            break;
          }
        }
      }

      if (this.table.filters['global'] && !globalMatch && globalFilterFieldsArray) {
        for (let j = 0; j < globalFilterFieldsArray.length; j++) {
          let globalFilterField = globalFilterFieldsArray[j].field || globalFilterFieldsArray[j];
          globalMatch = (<any>this.table.filterService).filters[(<any>this.table.filters['global']).matchMode](ObjectUtils.resolveFieldData(this.table.value[i], globalFilterField), (<FilterMetadata>this.table.filters['global']).value, this.table.filterLocale);

          if (globalMatch) {
            break;
          }
        }
      }

      let matches: boolean;
      if (this.table.filters['global']) {
        matches = localFiltered ? localFiltered && localMatch && globalMatch : globalMatch;
      } else {
        matches = localFiltered && localMatch;
      }

      if (matches) {
        this.table.filteredValue.push(this.table.value[i]);
      }
    }

    if (this.table.filteredValue.length === this.table.value.length) {
      this.table.filteredValue = null;
    }


  }

  executeLocalFilter(field: string, rowData: any, filterMeta: FilterMetadata): boolean {
    let filterValue = filterMeta.value;
    let filterMatchMode = filterMeta.matchMode || FilterMatchMode.STARTS_WITH;
    let dataFieldValue = ObjectUtils.resolveFieldData(rowData, field);
    // 處理 date 篩選, timeStamp 轉為 Date
    if([FilterMatchMode.DATE_IS, FilterMatchMode.DATE_IS_NOT, FilterMatchMode.DATE_BEFORE, FilterMatchMode.DATE_AFTER].includes(filterMeta.matchMode || '')) {
      dataFieldValue = new Date(dataFieldValue);
    }
    let filterConstraint = (<any>this.table.filterService).filters[filterMatchMode];

    return filterConstraint(dataFieldValue, filterValue, this.table.filterLocale);
  }

  addPaginatorBottomTemplate(): void {
    const paginatorBottom = this.el.nativeElement.querySelector('.p-paginator-bottom');
    if (!paginatorBottom) {
      return;
    }

    const findPDropDownElement = paginatorBottom.querySelector('p-dropdown');
    if (findPDropDownElement) {
      paginatorBottom.removeChild(findPDropDownElement);
    }

    const componentRef = this.viewContainerRef.createComponent(CustomRowsPerPageComponent);
    componentRef.instance.disabled = !this.table.value?.length;
    componentRef.instance.rowsPerPage = this.table.rows;
    componentRef.instance.rowsPerPageOptions = this.rowsPerPageOptions;
    componentRef.instance.syncRowsPerPage.subscribe((rowsPerPageOptions: number[]) => {
      this.rowsPerPageOptions = rowsPerPageOptions;
      this.table.saveState();
    });
    componentRef.instance.selectRowsPerPage.subscribe((rowsPerPage: number) => {
      /* 計算first與rows相除後的pageIndex，重新設定first應該在的位置 */
      const pageIndex = Math.floor((this.table.first || 0) / rowsPerPage);
      this.table.first = pageIndex * rowsPerPage;
      /* 同步元件內選擇的每頁筆數 */
      this.table.rows = rowsPerPage;
      this.setPageSize.emit({
        rows: this.table.rows
      });
      const event = {
        first: this.table.first,
        rows: this.table.rows,
      };
      this.table.onPage.emit(event);
      this.table.saveState();
    });
    this.customRowsPerPageComponent = componentRef.instance;
    this.renderer.appendChild(paginatorBottom, componentRef.location.nativeElement);
  }

  fetchDefaultRowsPerPageOptions(): void {
    this.commonDataService.searchCsmDetailSetting('TableRowsPerPageOption').subscribe((result) => {
      if (!result.length) { return; }
      
      const setting = result.find((x) => x.setupValue === this.tableKey);
      if (!setting || !setting.setupValue2) { return; }

      try {
        const options = JSON.parse(setting.setupValue2) as number[];
        this.rowsPerPageOptions = options.sort((a, b) => a - b);
        this.table.rowsPerPageOptions = this.rowsPerPageOptions;
      } catch (error) {
        console.error(error);
      }
    });
  }
}
